import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import { SubHeader } from "@/components/site/SubHeader";
import { Footer } from "@/components/site/Footer";
import { useLang } from "@/lib/i18n";
import { PHONE, PHONE_LABEL } from "@/lib/api";
import { buildPriceSchema } from "@/lib/priceSchema";
import { usePrices, quoteUrl, fmtDate } from "@/lib/prices";
import { useSeo, useJsonLd } from "@/lib/seo";
import { PriceFaq, faqSchema } from "@/components/site/PriceFaq";

const TXT = {
  no: { docTitle: "Priser – chiptuning og bilnøkler i Stavanger | RB TUN AS", desc: "Veiledende priser for chiptuning, ECU/TCU-kloning, ekstranøkler, smart key og alle nøkler mistet hos RB TUN AS i Stavanger.",
    back: "Tilbake", eyebrow: "Prisliste · Stavanger-regionen", h1a: "Veiledende", h1b: "priser.", col: "Tjeneste", colPrice: "Pris inkl. mva.", cta: "Få tilbud", updated: "Priser sist oppdatert", more: "Les mer",
    important: "Viktig", call: "Usikker på hva bilen din trenger? Ring oss", loadErr: "Prislisten kunne ikke lastes. Ring oss for pris." },
  en: { docTitle: "Prices – chiptuning and car keys in Stavanger | RB TUN AS", desc: "Indicative prices for chiptuning, ECU/TCU cloning, spare keys, smart keys and all keys lost at RB TUN AS in Stavanger.",
    back: "Back", eyebrow: "Price list · Stavanger region", h1a: "Indicative", h1b: "prices.", col: "Service", colPrice: "Price incl. VAT", cta: "Get a quote", updated: "Prices last updated", more: "Read more",
    important: "Important", call: "Not sure what your car needs? Call us", loadErr: "The price list could not be loaded. Call us for a price." },
};

const Row = ({ item, lang, t }) => {
  const nav = useNavigate();
  const name = (lang === "en" && item.name_en) || item.name_no;
  const quote = () => nav(quoteUrl(item.service, `${item.name_no} (${item.price})`));
  return (
    <div className="group grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-3 border-b border-white/10 py-5 transition-colors hover:bg-white/[0.02] sm:grid-cols-[1fr_auto_auto]" data-testid="price-row">
      <span className="text-base text-white sm:text-lg">{name}</span>
      <span className="whitespace-nowrap text-right font-display text-xl font-bold text-white sm:text-2xl" data-testid="price-value">{item.price}</span>
      <button type="button" onClick={quote} data-testid="price-quote-btn"
        className="col-span-2 flex items-center justify-center gap-2 border border-[var(--red)] px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[var(--red)] sm:col-span-1">
        {t.cta} <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
      </button>
    </div>
  );
};

const pageFor = (c) => {
  const s = c.items?.[0]?.service;
  return s === "bilnokkel" ? "/bilnokler" : s === "chiptuning" || s === "kloning" ? "/chiptuning" : null;
};

export default function Priser() {
  const { lang } = useLang();
  const t = TXT[lang] ?? TXT.no;
  const { data, err } = usePrices();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useSeo({ title: t.docTitle, desc: t.desc, path: "/priser" });
  useJsonLd("price-list-jsonld", data && buildPriceSchema(data));
  useJsonLd("price-faq-jsonld", faqSchema());
  const updated = fmtDate(data?.prices_updated_at);
  const cats = Array.isArray(data?.categories) ? data.categories : [];
  const note = (lang === "en" && data?.note_en) || data?.note_no;
  return (
    <div data-testid="prices-page">
      <SubHeader />
      <main className="mx-auto max-w-4xl px-5 py-16 sm:px-8 md:py-24">
        <div className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-[var(--red)]">{t.eyebrow}</div>
        <h1 className="font-display text-5xl font-black uppercase leading-[0.9] md:text-7xl">{t.h1a} <span className="text-[var(--red)]">{t.h1b}</span></h1>
        {updated && <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400" data-testid="prices-updated">{t.updated}: <span className="text-zinc-200">{updated}</span></p>}
        {err && <p className="mt-10 text-zinc-300" data-testid="prices-error">{t.loadErr}</p>}
        {cats.map((c) => (
          <section key={c.title_no} className="mt-14" data-testid="price-category">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="font-display text-2xl font-bold uppercase md:text-3xl">{(lang === "en" && c.title_en) || c.title_no}</h2>
              {pageFor(c) && (
                <Link to={pageFor(c)} data-testid={`price-category-link-${pageFor(c).slice(1)}`} className="inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-zinc-300 hover:text-white">
                  {t.more} <ArrowUpRight size={14} className="text-[var(--red)]" />
                </Link>
              )}
            </div>
            <div className="mt-4 flex justify-between border-b border-white/20 pb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
              <span>{t.col}</span><span className="sm:mr-[9.5rem]">{t.colPrice}</span>
            </div>
            {(c.items ?? []).map((it) => <Row key={it.name_no} item={it} lang={lang} t={t} />)}
          </section>
        ))}
        {note && (
          <p className="mt-12 border-l-2 border-[var(--red)] pl-4 text-sm leading-relaxed text-zinc-300" data-testid="prices-note">
            <b className="text-white">{t.important}:</b> {note}
          </p>
        )}
        <PriceFaq lang={lang} />
        <a href={`tel:${PHONE}`} className="mt-10 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-zinc-200 hover:text-white" data-testid="prices-phone">
          <Phone size={16} className="text-[var(--red)]" /> {t.call}: {PHONE_LABEL}
        </a>
      </main>
      <Footer />
    </div>
  );
}
